import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import {
	MeetingPrepSettingsUpdate,
	useMeetingPrepSettings,
	useMeetingPrepSettingsMutation,
} from '../../../../../queries';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { TextArea } from '../../../../components-v2/TextArea';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { styleSheet } from './styles';
import { DEFAULT_SEND_HOUR, getSendHourOptions } from './timeUtils';

interface IProps {
	onCancel?: () => void;
}

/** Form state mirrors the editable subset of the user's Meeting Prep settings. */
type FormState = MeetingPrepSettingsUpdate;

const getDefaultFormState = (settings?: Api.IMeetingPrepUserSettings): FormState => ({
	enabled: settings?.enabled ?? false,
	sendEmail: settings?.sendEmail ?? true,
	sendHourLocal: settings?.sendHourLocal ?? DEFAULT_SEND_HOUR,
	weekendDelivery: settings?.weekendDelivery ?? false,
	includeInternalAttendees: settings?.includeInternalAttendees ?? false,
	defaultPrompt: settings?.defaultPrompt ?? null,
});

// The API stores the send time as a whole hour (0–23), so the picker only offers on-the-hour options.
const SEND_HOUR_OPTIONS = getSendHourOptions();
// value↔label map for the Select root so the trigger shows the formatted label even while the
// dropdown is closed (Base UI's Select.Value can't resolve a label from unmounted items otherwise).
const SEND_HOUR_ITEMS = SEND_HOUR_OPTIONS.map(option => ({ label: option.label, value: String(option.value) }));

/**
 * Per-user Meeting Prep preferences form. Rendered inside a SettingsGroup that is only mounted when
 * the account has Meeting Prep enabled (see PersonalSettings/index.tsx). Reads/writes the dedicated
 * /MeetingPrep/preferences endpoint via react-query.
 */
export const MeetingPrepSettings: React.FC<IProps> = ({ onCancel }) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logEvent, logApiError } = useEventLogging('MeetingPrepSettings');

	const settingsQuery = useMeetingPrepSettings({
		userSession,
		meta: { toastErrorMessage: 'Failed to load your Meeting Prep settings.' },
	});

	const [form, setForm] = React.useState<FormState>(getDefaultFormState());

	// Seed the form once, when the settings first load. Reseeding on every `data` change would let a
	// background refetch (window focus/reconnect) clobber in-progress edits.
	const hasSeededRef = React.useRef(false);
	React.useEffect(() => {
		if (settingsQuery.data && !hasSeededRef.current) {
			hasSeededRef.current = true;
			setForm(getDefaultFormState(settingsQuery.data));
		}
	}, [settingsQuery.data]);

	const saveMutation = useMeetingPrepSettingsMutation({
		onSuccess: settings => {
			// Log what was actually persisted (the server response), not the latest form closure.
			logEvent('UpdateMeetingPrepSettings', { enabled: settings.enabled });
			// Briefs only surface later via email, so confirm the save explicitly.
			toast.success('Meeting Prep settings saved.');
		},
		onError: error => {
			logApiError('UpdateMeetingPrepSettings-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const updateField = <TKey extends keyof FormState>(key: TKey, value: FormState[TKey]) => {
		setForm(prev => ({ ...prev, [key]: value }));
	};

	// The master toggle drives both enabled and sendEmail — in v1 the only delivery channel is email. On
	// enable, restore the persisted sendEmail intent rather than hardcoding true, so a server-side
	// sendEmail=false isn't silently flipped by an off→on cycle within the session.
	const onToggleEnabled = (checked: boolean) => {
		setForm(prev => ({
			...prev,
			enabled: checked,
			sendEmail: checked ? (settingsQuery.data?.sendEmail ?? true) : prev.sendEmail,
		}));
	};

	const onSave = () => {
		saveMutation.mutate(form);
	};

	const onCancelClicked = () => {
		setForm(getDefaultFormState(settingsQuery.data ?? undefined));
		onCancel?.();
	};

	if (settingsQuery.isLoading) {
		return <LoadingSpinner type='small' />;
	}

	// Don't fall through to a defaults-seeded form on load failure — saving from it would overwrite the
	// user's real server settings with hardcoded defaults.
	if (settingsQuery.isError) {
		return (
			<div className={css(styleSheet.root)}>
				<span className={css(styleSheet.statusLine)}>
					We couldn’t load your Meeting Prep settings. Please refresh the page and try again.
				</span>
			</div>
		);
	}

	const lastRunDate = settingsQuery.data?.lastRunDate;

	return (
		<div className={css(styleSheet.root)}>
			<div className={css(styleSheet.row)}>
				<Checkbox
					checked={form.enabled}
					id='meeting-prep-enabled'
					onCheckedChange={value => onToggleEnabled(value === true)}
				/>
				<label className={css(styleSheet.label)} htmlFor='meeting-prep-enabled'>
					Email me a morning prep brief
				</label>
			</div>

			{form.enabled ? (
				<>
					<div className={css(styleSheet.field)}>
						<span className={css(styleSheet.label)}>Send time</span>
						<Select
							items={SEND_HOUR_ITEMS}
							value={String(form.sendHourLocal)}
							onValueChange={value => updateField('sendHourLocal', Number(value))}
						>
							<SelectTrigger styles={styleSheet.timeTrigger}>
								<SelectValue placeholder='Select a time' />
							</SelectTrigger>
							<SelectContent>
								{SEND_HOUR_OPTIONS.map(option => (
									<SelectItem key={option.value} value={String(option.value)}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className={css(styleSheet.row)}>
						<Checkbox
							checked={form.weekendDelivery}
							id='meeting-prep-weekend'
							onCheckedChange={value => updateField('weekendDelivery', value === true)}
						/>
						<label className={css(styleSheet.label)} htmlFor='meeting-prep-weekend'>
							Deliver on weekends
						</label>
					</div>

					<div className={css(styleSheet.field)}>
						<div className={css(styleSheet.row)}>
							<Checkbox
								checked={form.includeInternalAttendees}
								id='meeting-prep-internal'
								onCheckedChange={value => updateField('includeInternalAttendees', value === true)}
							/>
							<label className={css(styleSheet.label)} htmlFor='meeting-prep-internal'>
								Include internal attendees
							</label>
						</div>
						<span className={css(styleSheet.helpText)}>
							When on, meetings with only your colleagues get prepped too.
						</span>
					</div>

					<div className={css(styleSheet.field)}>
						<label className={css(styleSheet.label)} htmlFor='meeting-prep-prompt'>
							Default focus / tone
						</label>
						<TextArea
							id='meeting-prep-prompt'
							placeholder='Optional guidance applied to every brief (e.g. “Focus on renewals”).'
							value={form.defaultPrompt ?? ''}
							onChange={event => updateField('defaultPrompt', event.target.value || null)}
						/>
					</div>

					{lastRunDate ? (
						<span className={css(styleSheet.statusLine)}>
							Last run: {moment(lastRunDate).format('MMM D, YYYY [at] h:mm A')}
						</span>
					) : null}
				</>
			) : null}

			<div className={css(styleSheet.footer)}>
				<Button onClick={onSave} isLoading={saveMutation.isPending}>
					Save
				</Button>
				<Button variant='outline' onClick={onCancelClicked} disabled={saveMutation.isPending}>
					Cancel
				</Button>
			</div>
		</div>
	);
};
